/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { CardSkills, ProductCard, breakpoints } from '@coursera/cds-core';
import type {
  ProductCard as ProductCardTrackingData,
  Product as ProductTrackingData,
  ProductType,
  Recommender,
} from '@coursera/event-pulse-types';
import type { EventData } from '@coursera/event-pulse/react';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import SiteMiscExperiments from 'bundles/epic/clients/siteMisc';
import { PathwayContentBadge } from 'bundles/product-card/components/Badges';
import LearningProductCardBadge, { getCreditType } from 'bundles/product-card/components/LearningProductCardBadge';
import LearningProductCardFooter from 'bundles/product-card/components/LearningProductCardFooter';
import {
  capitalizeAndTranslateEachWordInEntityType,
  eventingV3ProductType,
  extractEventId,
} from 'bundles/product-card/components/utils/productCardV2Utils';
import useOnVisible from 'bundles/product-card/hooks/useOnVisible';
import {
  getAiDifferentiatorTagPreview,
  getAiSocialProofTagPreview,
  getFreshnessSignalTagPreview,
  getGenericSocialProofTagPreview,
  getOutcomesBasedTagPreview,
  isAiDifferentiatorSlug,
  isAiSocialProofSlug,
  isFreshnessSignalSlug,
  isGenericSocialProofSlug,
  isOutcomesBasedSlug,
  recordAiDifferentiatorTagImpression,
  recordAiSocialProofTagImpression,
  recordFreshnessSignalTagImpression,
  recordGenericSocialProofTagImpression,
  recordOutcomesBasedTagImpression,
  shouldShowAiDifferentiatorTag,
  shouldShowAiSocialProofTag,
  shouldShowFreshnessSignalTag,
  shouldShowGenericSocialProofTag,
  shouldShowOutcomesBasedTag,
} from 'bundles/product-card/utils/copyTestUtils';
import type { LEARNING_PRODUCTS } from 'bundles/search-common/constants';
import { getUpsellCopy } from 'bundles/stackability-common/stackability-common-index';

const styles = {
  leftAlignProductCardTags: css`
    ${breakpoints.up('md')} {
      .cds-ProductCard-listCard {
        .cds-ProductCard-statusTags {
          position: absolute;
          left: 0;
          right: unset;
          top: var(--cds-spacing-100);
          padding-left: var(--cds-spacing-100);
          max-width: 298px;
          z-index: 1;
        }
      }
    }
  `,

  rightAlignProductCardTags: css`
    ${breakpoints.up('sm')} {
      .cds-ProductCard-statusTagsOverlay {
        left: unset !important;
        right: 0;
      }
    }
  `,

  cardContainer: css`
    min-height: 200px;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    cursor: pointer;

    .cds-ProductCard-partnerLogos img {
      vertical-align: unset;
    }

    .cds-CommonCard-previewImage {
      position: relative;
    }
  `,

  listCardContainer: css`
    /* intentionally unset min-height for list card that was default from CDS */
    min-height: unset;

    .cds-ProductCard-base.cds-ProductCard-list {
      height: inherit;

      /* intentionally unset min-width for list card that was default from CDS */
      min-width: unset;
    }
  `,

  // Temporary: Lawrie mentioned .cds-CommonCard-creditType will be removed in the future
  creditTypeGapTest1: css`
    .cds-CommonCard-creditType {
      gap: 4px !important;
    }
  `,
};

type Tag = string | React.ReactElement;
type LearningProduct = (typeof LEARNING_PRODUCTS)[keyof typeof LEARNING_PRODUCTS];

type Props = {
  adminContent?: React.ReactNode;
  customMetadata?: string[];
  customOnClick?: () => void;
  ImageComponent?: React.ReactNode;
  name: string;
  linkProps?: { href?: string; target?: string; rel?: string; component?: React.ElementType };
  titleComponent?: 'h3' | 'h4';
  partners?: { id: string; logo?: string; name: string }[];
  skills?: string[];
  bodyContent?: React.ReactNode;
  numProductRatings?: number;
  avgProductRating?: number;
  productType?: LearningProduct;
  productDifficultyLevel?: string;
  productDuration?: string;
  statusTags?: { tags: Tag[]; tagNames?: string[] };
  preBadgeContent?: React.ReactNode;
  badgeConfig?: {
    hideCreditBadge?: boolean;
    productType?: LearningProduct;
    isPathwayContent?: boolean;
  };
  statusTagConfig?: {
    hideCreditStatusTag?: boolean;
    hideAllStatusTags?: boolean;
  };
  postBadgeContent?: React.ReactNode;
  variant?: 'list' | 'grid';
  size?: 'compact' | 'standard';
  id?: string;
  slug?: string;
  cardIndex?: number;
  indexName?: string;
  recommender?: Recommender;
  trackingProps?: {
    trackedLinkProps?: {
      trackingName: string;
      withVisibilityTracking: boolean;
      requireFullyVisible: boolean;
      data: Record<string, unknown>;
      refAlt?: React.RefObject<HTMLAnchorElement>;
    };
    additionalProductCardTrackingData?: Partial<ProductCardTrackingData>;
    additionalProductTrackingData?: Partial<ProductTrackingData>;
    additionalV3TrackingData?: Record<string, unknown>;
  };
  isMetadataAboveProductRating?: boolean;
  buttons?: Array<React.ReactElement>;
};

const LearningProductCard = ({
  adminContent,
  customMetadata,
  customOnClick,
  ImageComponent,
  name,
  titleComponent,
  linkProps,
  partners,
  skills,
  bodyContent,
  numProductRatings,
  avgProductRating,
  productType,
  productDifficultyLevel,
  productDuration,
  statusTags,
  preBadgeContent,
  badgeConfig,
  statusTagConfig,
  postBadgeContent,
  variant = 'grid',
  size = 'standard',
  id,
  slug,
  cardIndex,
  indexName,
  recommender,
  trackingProps,
  isMetadataAboveProductRating = false,
  buttons,
}: Props) => {
  const track = useTracker();

  const getTrackingProductId = (productId: string | undefined): string => {
    if (typeof productId !== 'string') {
      return '';
    }
    if (productId.startsWith('child~')) {
      return productId; // Preserve "child~" prefix for product IDs in tracking events
    }
    return extractEventId(productId) || '';
  };

  const experimentVariant = SiteMiscExperiments.preview('productCardTest1.1');
  const shouldUseColorCodedPillTags = GrowthDiscoveryEpicClient.get('colorCodedPillTags');

  const cardMetadata =
    customMetadata ||
    [productDifficultyLevel, capitalizeAndTranslateEachWordInEntityType(productType), productDuration].filter(Boolean);

  // Rendering list cards with even a single status tag can cause styling issues with longer-typed
  // languages for desktop widths(see https://coursera.atlassian.net/browse/MERCH-1660). As a
  // workaround, we left-align tags and assign a max-width slightly less than the preview image.
  const listCardWithStatusTags = variant === 'list' && statusTags?.tags?.length && statusTags?.tags?.length >= 1;

  // Uses preview() for all EPIC calls so no impression is recorded during render.
  // Impressions are recorded in the useOnVisible callback below once the card scrolls into view.
  const getStatusTags = (): { tags: Tag[]; tagNames?: string[] } | undefined => {
    if (statusTagConfig?.hideAllStatusTags) {
      return undefined;
    }

    if (!GrowthDiscoveryEpicClient.preview('isStatusTagCopyTestRunning')) {
      if (shouldUseColorCodedPillTags) {
        return undefined;
      }
      return statusTags;
    }

    if (!GrowthDiscoveryEpicClient.preview('isNotInHoldoutGroup')) {
      // users in the holdout group should not see any status tags
      // this group essentially just exists for statistical analysis
      return undefined;
    }

    if (shouldShowOutcomesBasedTag(slug ?? '')) {
      const outcomesBasedTag = getOutcomesBasedTagPreview();
      if (outcomesBasedTag) {
        return { tags: [outcomesBasedTag.tag], tagNames: [outcomesBasedTag.tagName] };
      }
    }

    if (shouldShowAiDifferentiatorTag(slug ?? '')) {
      const aiDifferentiatorTag = getAiDifferentiatorTagPreview();
      if (aiDifferentiatorTag) {
        return { tags: [aiDifferentiatorTag.tag], tagNames: [aiDifferentiatorTag.tagName] };
      }
    }

    if (shouldShowAiSocialProofTag(slug ?? '')) {
      const aiSocialProofTag = getAiSocialProofTagPreview();
      if (aiSocialProofTag) {
        return { tags: [aiSocialProofTag.tag], tagNames: [aiSocialProofTag.tagName] };
      }
    }

    if (shouldShowGenericSocialProofTag(slug ?? '')) {
      const genericSocialProofTag = getGenericSocialProofTagPreview();
      if (genericSocialProofTag) {
        return { tags: [genericSocialProofTag.tag], tagNames: [genericSocialProofTag.tagName] };
      }
    }

    if (shouldShowFreshnessSignalTag(slug ?? '')) {
      const freshnessSignalTag = getFreshnessSignalTagPreview();
      if (freshnessSignalTag) {
        return { tags: [freshnessSignalTag.tag], tagNames: [freshnessSignalTag.tagName] };
      }
    }

    return undefined;
  };

  const trackingData: EventData<'click_product_card'> = {
    product: {
      id: getTrackingProductId(id),
      name,
      slug: slug ?? linkProps?.href ?? '',
      type: eventingV3ProductType(productType) as ProductType,
      ...trackingProps?.additionalProductTrackingData,
    },
    productCard: {
      ...(typeof trackingProps?.trackedLinkProps?.data.productCard === 'object'
        ? trackingProps?.trackedLinkProps?.data.productCard
        : {}),
      index: cardIndex ?? 0,
      ...(indexName && { algoliaIndexName: indexName }),
      ...(recommender && { recommender }),
      tags: getStatusTags()?.tagNames,
      ...(trackingProps?.additionalProductCardTrackingData?.pageConfigComponent && {
        pageConfigComponent: trackingProps?.additionalProductCardTrackingData?.pageConfigComponent,
      }),
    },
    ...trackingProps?.additionalV3TrackingData,
  };

  const trackedCardRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_product_card',
    trackingData,
    [],
    { fullyVisible: true, initialDelay: 1000 }
  );

  useOnVisible(
    trackedCardRef,
    () => {
      // Impression: get() returns same variant as preview() but records the experiment show event
      SiteMiscExperiments.get('productCardTest1.1');

      // Record impressions for the copy test experiments only once the card is actually visible.
      if (!GrowthDiscoveryEpicClient.get('isStatusTagCopyTestRunning')) return;
      if (!GrowthDiscoveryEpicClient.get('isNotInHoldoutGroup')) return;

      const currentSlug = slug ?? '';
      if (isOutcomesBasedSlug(currentSlug)) {
        recordOutcomesBasedTagImpression();
      } else if (isAiDifferentiatorSlug(currentSlug)) {
        recordAiDifferentiatorTagImpression();
      } else if (isAiSocialProofSlug(currentSlug)) {
        recordAiSocialProofTagImpression();
      } else if (isGenericSocialProofSlug(currentSlug)) {
        recordGenericSocialProofTagImpression();
      } else if (isFreshnessSignalSlug(currentSlug)) {
        recordFreshnessSignalTagImpression();
      }
    },
    { fullyVisible: true, initialDelay: 1000, once: true }
  );

  const combinedBadge = (
    <>
      {preBadgeContent}
      {!shouldUseColorCodedPillTags && !badgeConfig?.hideCreditBadge && (
        <LearningProductCardBadge
          customBadge={badgeConfig?.isPathwayContent ? <PathwayContentBadge /> : undefined}
          productType={badgeConfig?.productType}
        />
      )}
      {postBadgeContent}
    </>
  );

  const creditBadgePillTag = !statusTagConfig?.hideCreditStatusTag && getCreditType(badgeConfig?.productType).label;

  const getStatusTagsForFooter = () => {
    if (!shouldUseColorCodedPillTags || statusTagConfig?.hideAllStatusTags) {
      return undefined;
    }
    let tagNames = statusTags?.tagNames ?? [];
    if (badgeConfig?.isPathwayContent) {
      tagNames = [...tagNames, getUpsellCopy().homepagesNavXdp.buildTowardADegree];
    } else if (creditBadgePillTag) {
      tagNames = [...tagNames, creditBadgePillTag];
    }
    return tagNames;
  };

  return (
    <div
      css={[
        styles.cardContainer,
        listCardWithStatusTags ? styles.leftAlignProductCardTags : styles.rightAlignProductCardTags,
        variant === 'list' ? styles.listCardContainer : null,
        experimentVariant !== 'control' ? styles.creditTypeGapTest1 : null,
      ]}
      data-testid="product-card-cds"
    >
      <ProductCard
        experimentVariant={experimentVariant}
        onClick={(event) => {
          track('click_product_card', trackingData);
          if (linkProps?.href) {
            return;
          }
          if (customOnClick) {
            event.preventDefault();
            event.stopPropagation();
            customOnClick();
          }
        }}
        ref={trackedCardRef}
        renderPreviewImage={() => ImageComponent}
        title={{
          name,
          linkProps,
          component: titleComponent,
          customLinkProps: trackingProps?.trackedLinkProps,
        }}
        partners={partners}
        body={
          (skills && skills.length > 0) || bodyContent ? (
            <>
              {skills && skills.length > 0 && <CardSkills skills={skills} />}
              {bodyContent}
            </>
          ) : undefined
        }
        footer={
          <LearningProductCardFooter
            isMetadataAboveProductRating={isMetadataAboveProductRating}
            cardMetadata={cardMetadata}
            avgProductRating={avgProductRating}
            numProductRatings={numProductRatings}
            badge={combinedBadge}
            shouldUseColorCodedPillTags={shouldUseColorCodedPillTags}
            statusTagNames={getStatusTagsForFooter()}
            buttons={buttons}
          />
        }
        variant={variant}
        size={size}
        productType={productType || ''}
        statusTags={getStatusTags()?.tags}
        adminContent={adminContent}
      />
    </div>
  );
};

export default LearningProductCard;
